import LegalLayout from "@/components/LegalLayout";

/**
 * Terms of Service SaaS per RecenUp (Free / Pro / Trial).
 * Le condizioni di vendita delle NFC Card sono in un documento separato:
 * `/condizioni-vendita`.
 */
export default function Terms() {
  return (
    <LegalLayout title="Termini di Servizio" updatedAt="Febbraio 2026">
      <p>
        I presenti Termini di Servizio ("<strong>Termini</strong>") disciplinano l'uso
        della piattaforma RecenUp ("<strong>App</strong>", "<strong>Servizio</strong>")
        fornita da <strong>[DA COMPILARE: ragione sociale]</strong> ("<strong>RecenUp</strong>", "noi").
        Utilizzando il Servizio l'Utente ("tu", "l'Utente") accetta integralmente i Termini.
        Per le condizioni di acquisto delle NFC Card fisiche si veda il documento{" "}
        <a href="/condizioni-vendita">Condizioni di Vendita</a>.
      </p>

      <h2>1. Descrizione del servizio</h2>
      <p>
        RecenUp è un'applicazione SaaS che consente ai titolari di attività commerciali di:
      </p>
      <ul>
        <li>Monitorare le recensioni pubbliche di Google Business Profile.</li>
        <li>Sincronizzare automaticamente le recensioni previa autorizzazione OAuth dell'account Google.</li>
        <li>Ricevere analytics avanzati, insight AI, risposte AI suggerite e report programmati (funzionalità del piano Pro).</li>
        <li>Gestire richieste e attivazioni di NFC Card brandizzate collegate a Google Reviews.</li>
        <li>Partecipare al programma "Invita & Guadagna" con accredito di crediti in caso di conversione.</li>
      </ul>

      <h2>2. Registrazione e account</h2>
      <ul>
        <li>La registrazione richiede un indirizzo email valido e una password (minimo 12 caratteri, con blocco automatico delle password più comuni).</li>
        <li>L'Utente è responsabile della riservatezza delle proprie credenziali e di ogni attività effettuata tramite il proprio account.</li>
        <li>È vietata la registrazione con dati falsi o l'utilizzo dell'account di terzi.</li>
        <li>RecenUp può sospendere o chiudere account che violino i Termini, con preavviso quando ragionevolmente possibile.</li>
      </ul>

      <h2>3. Piani, prezzi e modalità di pagamento</h2>
      <h3>3.1 Piano Free</h3>
      <ul>
        <li>Gratuito, senza scadenza.</li>
        <li>Limiti attualmente in vigore: 1 attività, 1 NFC Card attiva, 1 Google Business Profile collegato.</li>
        <li>Nessuna funzione AI, nessun analytics avanzato, nessun report programmato.</li>
      </ul>
      <h3>3.2 Trial Pro (14 giorni)</h3>
      <ul>
        <li>Attivabile una sola volta per utente, senza necessità di inserire una carta.</li>
        <li>Durata: 14 giorni solari a partire dall'attivazione.</li>
        <li>Al termine, in assenza di attivazione dell'abbonamento Pro, l'account torna automaticamente al piano Free; i dati restano accessibili in modalità Free.</li>
      </ul>
      <h3>3.3 Piano Pro</h3>
      <ul>
        <li>Prezzo: <strong>20,00 EUR/mese</strong> (IVA inclusa ove applicabile), addebito ricorrente mensile.</li>
        <li>Modalità di pagamento: carta di credito/debito tramite Stripe.</li>
        <li>Rinnovo automatico salvo disdetta.</li>
        <li>Ai sensi dell'art. 59 lett. o) del D.Lgs. 206/2005 (Codice del Consumo) l'Utente consumatore acconsente all'esecuzione immediata del servizio digitale e rinuncia al diritto di recesso di 14 giorni <em>una volta iniziata l'erogazione</em>. Per il periodo residuo non goduto non è previsto rimborso, salvo diverse disposizioni di legge o accordi commerciali specifici.</li>
      </ul>
      <h3>3.4 Gestione abbonamento</h3>
      <p>
        L'Utente può in qualsiasi momento gestire, sospendere o cancellare l'abbonamento
        dal proprio profilo (sezione "Piano") accedendo al portale Stripe integrato. La
        cancellazione ha effetto alla fine del periodo di fatturazione corrente.
      </p>

      <h2>4. Funzioni AI (solo Pro / Trial)</h2>
      <ul>
        <li>Le funzioni "AI Insights" e "AI Reply" sono soggette a una <strong>quota mensile di 100 utilizzi</strong> per utente (configurabile).</li>
        <li>L'Utente prende atto che le proposte generate da AI sono <strong>suggerimenti automatici</strong> e non sono mai pubblicate senza sua conferma esplicita.</li>
        <li>RecenUp non garantisce la correttezza, completezza o adeguatezza dei contenuti generati; l'Utente resta unico responsabile di ciò che pubblica sulle piattaforme esterne.</li>
      </ul>

      <h2>5. Uso ammesso</h2>
      <p>L'Utente si impegna a NON:</p>
      <ul>
        <li>Utilizzare il Servizio per finalità illegali o contrarie a buon costume.</li>
        <li>Effettuare reverse engineering, scraping massivo o abuso delle API.</li>
        <li>Falsificare, manipolare o incentivare recensioni false su Google o altre piattaforme (pratica vietata dai Termini di Google e dalla normativa consumeristica UE — direttiva "Omnibus" 2019/2161, recepita in Italia con D.Lgs. 26/2023).</li>
        <li>Utilizzare il Servizio per inviare comunicazioni non richieste (spam) o violare la privacy di terzi.</li>
        <li>Rivendere il Servizio a terzi senza accordo scritto con RecenUp.</li>
      </ul>

      <h2>6. Programma referral</h2>
      <ul>
        <li>Ogni Utente registrato riceve un codice referral personale.</li>
        <li>La conversione in credito avviene solo quando l'invitato completa il primo pagamento e trascorrono almeno 14 giorni senza rimborso.</li>
        <li>Il credito viene applicato automaticamente al checkout successivo. Non è convertibile in denaro né trasferibile.</li>
        <li>RecenUp si riserva il diritto di annullare crediti generati da comportamenti fraudolenti o abusivi.</li>
      </ul>

      <h2>7. Google Business Profile</h2>
      <ul>
        <li>Il collegamento OAuth a Google Business Profile è facoltativo.</li>
        <li>L'Utente autorizza RecenUp a leggere, elencare e sincronizzare le recensioni pubbliche delle proprie location.</li>
        <li>Il collegamento è revocabile in qualsiasi momento dalla sezione Profilo → "Scollega Google". Le recensioni già sincronizzate rimarranno storicizzate.</li>
      </ul>

      <h2>8. Contenuti dell'Utente</h2>
      <p>
        L'Utente resta unico titolare dei dati e dei contenuti caricati nell'App. Concede
        a RecenUp una licenza limitata, non esclusiva, revocabile per il solo scopo di
        erogare il Servizio (memorizzare, elaborare, mostrare i dati all'Utente stesso e
        ai collaboratori da lui autorizzati).
      </p>

      <h2>9. Disponibilità del servizio e SLA</h2>
      <p>
        RecenUp opera secondo un modello "best effort" senza SLA contrattuale sulla
        disponibilità. Sono possibili interruzioni per manutenzione ordinaria, aggiornamenti
        di sicurezza o cause di forza maggiore. In caso di indisponibilità superiore a 24
        ore consecutive per causa imputabile a RecenUp, l'Utente Pro può richiedere un
        credito proporzionale al periodo di disservizio.
      </p>

      <h2>10. Limitazione di responsabilità</h2>
      <p>
        Nei limiti massimi consentiti dalla legge, RecenUp non risponde di danni indiretti,
        mancati guadagni, perdita di reputazione, perdita di dati non imputabile a colpa
        grave o dolo. In ogni caso, la responsabilità complessiva di RecenUp verso
        l'Utente non potrà eccedere l'importo pagato dall'Utente nei 12 mesi precedenti
        l'evento dannoso.
      </p>

      <h2>11. Modifiche ai Termini</h2>
      <p>
        RecenUp può modificare i presenti Termini con preavviso di almeno 15 giorni via
        email agli Utenti registrati. Le modifiche sostanziali che aumentano gli obblighi
        dell'Utente danno diritto al recesso dal servizio senza penalità entro tale
        preavviso.
      </p>

      <h2>12. Legge applicabile e foro competente</h2>
      <p>
        I presenti Termini sono regolati dalla legge italiana. Per le controversie che
        coinvolgono un Utente consumatore, il foro competente è quello del luogo di
        residenza o domicilio del consumatore ex art. 66-bis del Codice del Consumo. Per
        gli Utenti professionali il foro competente è quello di <strong>[DA COMPILARE: sede
        legale del Titolare]</strong>.
      </p>

      <h2>13. Contatti</h2>
      <p>
        Per qualsiasi comunicazione relativa ai presenti Termini scrivere a{" "}
        <a href="mailto:privacy@recenup.com">privacy@recenup.com</a>.
      </p>
    </LegalLayout>
  );
}
